import { debounce } from 'lodash';
import { snackbarService } from '@one/web-components/dist/index.cjs.js';
import { DURATION } from '../../constants';
import {
  PROCESSING_RESULTS,
  NOTIFICATION_TYPES,
  DEFAULT_MESSAGES,
} from './types';
import { quickEditTracker } from './quickEditTracker';

/**
 * BookingOperationsService - Centralized service for handling all booking operations
 */
class BookingOperationsService {
  constructor() {
    // Store debounced callbacks to prevent memory leaks
    this.debouncedCallbacks = new Map();
  }

  /**
   * Get the appropriate message for a processing result
   * @param {string} processingResult - The processing result from the backend
   * @param {string} operation - The operation type
   * @param {object} customMessages - Custom messages to override defaults
   * @returns {object} Message object with type and message
   */
  getNotificationMessage(processingResult, operation, customMessages = {}) {
    // Check for custom message first
    if (customMessages[processingResult]) {
      return {
        type: this.getNotificationType(processingResult),
        message: customMessages[processingResult],
        duration: DURATION,
      };
    }

    const messages = DEFAULT_MESSAGES[processingResult] || {};
    const message = messages[operation] || messages.default || 'Operation completed.';

    return {
      type: this.getNotificationType(processingResult),
      message,
      duration: DURATION,
    };
  }

  /**
   * Get notification type based on processing result
   * @param {string} processingResult - The processing result
   * @returns {string} Notification type
   */
  getNotificationType(processingResult) {
    switch (processingResult) {
      case PROCESSING_RESULTS.ALL_SUCCEEDED:
      case PROCESSING_RESULTS.APPROVED_SUCCEEDED:
      case PROCESSING_RESULTS.REJECTED_SUCCEEDED:
      case PROCESSING_RESULTS.CHECK_IN_SUCCEEDED:
        return NOTIFICATION_TYPES.SUCCESS;
      case PROCESSING_RESULTS.SOME_FAILED:
        return NOTIFICATION_TYPES.WARNING;
      case PROCESSING_RESULTS.ALL_FAILED:
        return NOTIFICATION_TYPES.ERROR;
      default:
        return NOTIFICATION_TYPES.INFO;
    }
  }

  /**
   * Handle the booking operation response
   * @param {object} data - Response data from GraphQL
   * @param {object} config - Operation configuration
   * @param {object} callbacks - Success/error callbacks
   */
  handleResponse(data, config, callbacks = {}) {
    if (!data || typeof data !== 'object') {
      console.warn('bookingOperationsService: Invalid data received', data);
      if (callbacks.onError) {
        callbacks.onError(new Error('Invalid data structure'));
      }
      return;
    }

    const processingResult = data?.data?.onUpdateBookingCommand?.processingResult;
    const commandId = data?.data?.onUpdateBookingCommand?.id;

    if (!processingResult) {
      if (callbacks.onError) {
        callbacks.onError(new Error('No processing result received'));
      }
      return;
    }

    // Skip CHECK_IN_SUCCEEDED if configured
    if (config.skipCheckInSucceeded && processingResult === PROCESSING_RESULTS.CHECK_IN_SUCCEEDED) {
      return;
    }

    let isQuickEdit = false;
    let operation = config.operation;

    if (commandId && quickEditTracker.isQuickEdit(commandId)) {
      isQuickEdit = true;
      const quickEditOperation = quickEditTracker.getOperation(commandId);
      if (quickEditOperation) {
        operation = quickEditOperation;
      }
    }
    if (!config.skipNotification) {
      const notification = this.getNotificationMessage(
        processingResult,
        operation,
        config.customMessages
      );
      snackbarService.show(notification);
    }

    if (isQuickEdit) {
      const isSuccess = [
        PROCESSING_RESULTS.ALL_SUCCEEDED,
        PROCESSING_RESULTS.APPROVED_SUCCEEDED,
        PROCESSING_RESULTS.REJECTED_SUCCEEDED,
        PROCESSING_RESULTS.CHECK_IN_SUCCEEDED,
      ].includes(processingResult);

      if (isSuccess) {
        quickEditTracker.resolve(commandId, data);
      } else {
        const message = this.getNotificationMessage(processingResult, operation, config.customMessages).message;
        quickEditTracker.reject(commandId, new Error(message));
      }

      quickEditTracker.remove(commandId);
    }

    // Call appropriate callback
    const isSuccess = [
      PROCESSING_RESULTS.ALL_SUCCEEDED,
      PROCESSING_RESULTS.APPROVED_SUCCEEDED,
      PROCESSING_RESULTS.REJECTED_SUCCEEDED,
      PROCESSING_RESULTS.CHECK_IN_SUCCEEDED,
    ].includes(processingResult);

    if (isSuccess && callbacks.onSuccess) {
      callbacks.onSuccess(data);
    } else if (!isSuccess && callbacks.onError) {
      callbacks.onError(data);
    }
  }

  /**
   * Create a debounced callback for booking operations
   * @param {string} key - Unique key for this callback
   * @param {object} config - Operation configuration
   * @param {object} callbacks - Success/error callbacks
   * @returns {function} Debounced callback function
   */
  createDebouncedCallback(key, config, callbacks) {
    // Clean up existing callback if it exists
    if (this.debouncedCallbacks.has(key)) {
      this.debouncedCallbacks.get(key).cancel();
    }

    const debouncedFn = debounce(
      (data) => this.handleResponse(data, config, callbacks),
      config.debounceDelay || 2500
    );

    this.debouncedCallbacks.set(key, debouncedFn);
    return debouncedFn;
  }

  /**
   * Create a callback for booking operations
   * @param {object} config - Operation configuration
   * @param {object} callbacks - Success/error callbacks
   * @returns {function} Callback function
   */
  createCallback(config, callbacks) {
    if (config.debounce !== false) {
      // Generate a unique key based on the config
      const key = `${config.operation}-${Date.now()}`;
      return this.createDebouncedCallback(key, config, callbacks);
    }

    return (data) => this.handleResponse(data, config, callbacks);
  }

  /**
   * Clean up debounced callbacks
   * @param {string} key - Optional key to clean up specific callback
   */
  cleanup(key) {
    if (key && this.debouncedCallbacks.has(key)) {
      this.debouncedCallbacks.get(key).cancel();
      this.debouncedCallbacks.delete(key);
    } else if (!key) {
      // Clean up all callbacks
      this.debouncedCallbacks.forEach((callback) => callback.cancel());
      this.debouncedCallbacks.clear();
    }
  }
}

// Export singleton instance
export const bookingOperationsService = new BookingOperationsService();

// Also export the class for testing
export { BookingOperationsService };
